import { useRef, useEffect, useState, useCallback } from "react";
import "./RichTextEditor.css";

/* HTML execCommand fontSize accepts values 1–7 only.
   Labels show the visual pixel equivalent so users see real numbers. */
const FONT_SIZES = [
  { label: "10", value: "1" },
  { label: "13", value: "2" },
  { label: "16", value: "3" },
  { label: "18", value: "4" },
  { label: "24", value: "5" },
  { label: "32", value: "6" },
  { label: "48", value: "7" },
];

/* Curated palette — neutral + corporate + accent rows. */
const PRESET_COLORS = [
  "#0f172a", "#1e293b", "#475569", "#94a3b8", "#cbd5e1", "#ffffff",
  "#016394", "#0891b2", "#1d4ed8", "#7c3aed", "#db2777", "#eb4e39",
  "#dc2626", "#ea580c", "#ca8a04", "#16a34a", "#0d9488", "#8a8c8e",
];

function rgbToHex(value) {
  if (!value) return null;
  if (value.startsWith("#")) return value.toLowerCase();
  const m = value.match(/\d+/g);
  if (!m || m.length < 3) return null;
  return (
    "#" +
    m.slice(0, 3)
      .map(n => parseInt(n, 10).toString(16).padStart(2, "0"))
      .join("")
  );
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = "",
  minRows = 12,
}) {
  const editorRef = useRef(null);
  const lastEmittedHtml = useRef("");
  const colorWrapRef = useRef(null);

  const [showColors, setShowColors] = useState(false);
  const [currentColor, setCurrentColor] = useState("#1e293b");
  const [recentColors, setRecentColors] = useState([]);
  const [active, setActive] = useState({
    bold: false, italic: false, underline: false, strikeThrough: false,
  });

  /* Sync external value into the contentEditable element only when it
     differs from what we last emitted — otherwise React re-renders
     would reset the caret on every keystroke. */
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value !== lastEmittedHtml.current && value !== el.innerHTML) {
      el.innerHTML = value || "";
      lastEmittedHtml.current = value || "";
    }
  }, [value]);

  const updateActiveStates = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const active = document.activeElement === el || el.contains(document.activeElement);
    if (!active) return;
    try {
      setActive({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        strikeThrough: document.queryCommandState("strikeThrough"),
      });
      const c = rgbToHex(document.queryCommandValue("foreColor"));
      if (c) setCurrentColor(c);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", updateActiveStates);
    return () => document.removeEventListener("selectionchange", updateActiveStates);
  }, [updateActiveStates]);

  /* Close color popover on outside click */
  useEffect(() => {
    if (!showColors) return;
    function handler(e) {
      if (!colorWrapRef.current) return;
      if (!colorWrapRef.current.contains(e.target)) setShowColors(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showColors]);

  function emit() {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML;
    lastEmittedHtml.current = html;
    onChange(html);
  }

  function exec(command, val = null) {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    emit();
    updateActiveStates();
  }

  function pickColor(color, { closePanel = true } = {}) {
    setCurrentColor(color);
    setRecentColors(prev => {
      const next = [color, ...prev.filter(c => c !== color)].slice(0, 6);
      return next;
    });
    exec("foreColor", color);
    if (closePanel) setShowColors(false);
  }

  function handleLink() {
    const sel = window.getSelection();
    const selected = sel ? sel.toString() : "";
    const url = window.prompt(
      selected
        ? `მონიშნული ტექსტი: "${selected}"\n\nშეიყვანეთ ბმული:`
        : "შეიყვანეთ ბმული:",
      "https://"
    );
    if (url && url.trim()) exec("createLink", url.trim());
  }

  /* Prevent toolbar mousedown from stealing focus from editor — but allow
     <select> and <input> to receive native interaction. */
  function preserveFocus(e) {
    const tag = e.target.tagName;
    if (tag !== "SELECT" && tag !== "INPUT" && tag !== "OPTION") {
      e.preventDefault();
    }
  }

  const isEmpty = !value || value.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, "").trim() === "";

  return (
    <div className="rte">
      <div className="rte-toolbar" onMouseDown={preserveFocus}>
        <ToolGroup>
          <ToolBtn label="გასქელება" onClick={() => exec("bold")} active={active.bold} title="გასქელება (Ctrl+B)">
            <span className="rte-letter rte-letter-bold">B</span>
          </ToolBtn>
          <ToolBtn label="დახრილი" onClick={() => exec("italic")} active={active.italic} title="დახრილი (Ctrl+I)">
            <span className="rte-letter rte-letter-italic">I</span>
          </ToolBtn>
          <ToolBtn label="ხაზგასმა" onClick={() => exec("underline")} active={active.underline} title="ხაზგასმა (Ctrl+U)">
            <span className="rte-letter rte-letter-underline">U</span>
          </ToolBtn>
          <ToolBtn label="გადახაზვა" onClick={() => exec("strikeThrough")} active={active.strikeThrough} title="გადახაზვა">
            <span className="rte-letter rte-letter-strike">S</span>
          </ToolBtn>
        </ToolGroup>

        <ToolDivider />

        <div className="rte-size">
          <select
            className="rte-size-select"
            onChange={(e) => {
              if (e.target.value) {
                exec("fontSize", e.target.value);
                e.target.value = "";
              }
            }}
            defaultValue=""
            title="ზომა"
          >
            <option value="" disabled hidden>ზომა</option>
            {FONT_SIZES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <ChevronIcon className="rte-size-chevron" />
        </div>

        <ToolDivider />

        <div className="rte-color-wrap" ref={colorWrapRef}>
          <button
            type="button"
            className="rte-btn rte-color-btn"
            onClick={() => setShowColors(v => !v)}
            title="ფერი"
            aria-haspopup="dialog"
            aria-expanded={showColors}
          >
            <span className="rte-color-letter">A</span>
            <span className="rte-color-bar" style={{ background: currentColor }} />
            <ChevronIcon className="rte-btn-chevron" />
          </button>

          {showColors && (
            <div className="rte-color-pop" role="dialog" aria-label="ფერის არჩევა">
              <div className="rte-color-pop-section">
                <div className="rte-color-pop-title">სტანდარტული</div>
                <div className="rte-color-grid">
                  {PRESET_COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      className={`rte-swatch${currentColor === c.toLowerCase() ? " is-selected" : ""}`}
                      style={{ background: c }}
                      onClick={() => pickColor(c)}
                      title={c}
                      aria-label={c}
                    />
                  ))}
                </div>
              </div>

              {recentColors.length > 0 && (
                <div className="rte-color-pop-section">
                  <div className="rte-color-pop-title">ბოლო გამოყენებული</div>
                  <div className="rte-color-grid rte-color-grid-recent">
                    {recentColors.map((c, i) => (
                      <button
                        key={`r-${i}-${c}`}
                        type="button"
                        className="rte-swatch"
                        style={{ background: c }}
                        onClick={() => pickColor(c)}
                        title={c}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="rte-color-pop-divider" />

              <label className="rte-color-custom" title="აირჩიე ნებისმიერი ფერი">
                <span
                  className="rte-color-custom-preview"
                  style={{ background: currentColor }}
                  aria-hidden="true"
                />
                <span className="rte-color-custom-label">
                  ნებისმიერი ფერი
                  <span className="rte-color-custom-hex">{currentColor}</span>
                </span>
                <input
                  type="color"
                  className="rte-color-custom-input"
                  value={currentColor}
                  onChange={(e) => pickColor(e.target.value, { closePanel: false })}
                />
              </label>
            </div>
          )}
        </div>

        <ToolDivider />

        <ToolGroup>
          <ToolBtn label="ბულეტ-სია" onClick={() => exec("insertUnorderedList")} title="ბულეტ-სია">
            <BulletListIcon />
          </ToolBtn>
          <ToolBtn label="დანომრილი სია" onClick={() => exec("insertOrderedList")} title="დანომრილი სია">
            <NumberedListIcon />
          </ToolBtn>
        </ToolGroup>

        <ToolDivider />

        <ToolGroup>
          <ToolBtn label="მარცხნივ" onClick={() => exec("justifyLeft")} title="მარცხნიდან">
            <AlignLeftIcon />
          </ToolBtn>
          <ToolBtn label="ცენტრი" onClick={() => exec("justifyCenter")} title="ცენტრში">
            <AlignCenterIcon />
          </ToolBtn>
          <ToolBtn label="მარჯვნივ" onClick={() => exec("justifyRight")} title="მარჯვნიდან">
            <AlignRightIcon />
          </ToolBtn>
        </ToolGroup>

        <ToolDivider />

        <ToolGroup>
          <ToolBtn label="ბმული" onClick={handleLink} title="ბმული">
            <LinkIcon />
          </ToolBtn>
          <ToolBtn label="ფორმატის გასუფთავება" onClick={() => exec("removeFormat")} title="ფორმატის გასუფთავება">
            <ClearIcon />
          </ToolBtn>
        </ToolGroup>
      </div>

      <div className="rte-area">
        {isEmpty && placeholder && <div className="rte-placeholder">{placeholder}</div>}
        <div
          ref={editorRef}
          className="rte-content"
          contentEditable
          onInput={emit}
          onBlur={emit}
          onKeyUp={updateActiveStates}
          onMouseUp={updateActiveStates}
          style={{ minHeight: `${minRows * 1.5}em` }}
          suppressContentEditableWarning
        />
      </div>
    </div>
  );
}

/* ─── Sub-components ─── */

function ToolGroup({ children }) {
  return <div className="rte-group">{children}</div>;
}

function ToolDivider() {
  return <span className="rte-divider" aria-hidden="true" />;
}

function ToolBtn({ children, onClick, active, title, label }) {
  return (
    <button
      type="button"
      className={`rte-btn${active ? " is-active" : ""}`}
      onClick={onClick}
      title={title}
      aria-label={label || title}
      aria-pressed={active || undefined}
    >
      {children}
    </button>
  );
}

/* ─── Icons (14×14, currentColor) ─── */

const ChevronIcon = ({ className }) => (
  <svg className={className} width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path d="M2.5 4l2.5 2.5L7.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BulletListIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <circle cx="2.5" cy="3.5" r="1.1" fill="currentColor" />
    <circle cx="2.5" cy="7.5" r="1.1" fill="currentColor" />
    <circle cx="2.5" cy="11.5" r="1.1" fill="currentColor" />
    <line x1="6" y1="3.5" x2="13.5" y2="3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="6" y1="7.5" x2="13.5" y2="7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="6" y1="11.5" x2="13.5" y2="11.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const NumberedListIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <text x="0.5" y="5" fontSize="4.2" fontWeight="700" fill="currentColor" fontFamily="-apple-system, sans-serif">1.</text>
    <text x="0.5" y="9.5" fontSize="4.2" fontWeight="700" fill="currentColor" fontFamily="-apple-system, sans-serif">2.</text>
    <text x="0.5" y="14" fontSize="4.2" fontWeight="700" fill="currentColor" fontFamily="-apple-system, sans-serif">3.</text>
    <line x1="6" y1="3.5" x2="13.5" y2="3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="6" y1="8" x2="13.5" y2="8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="6" y1="12.5" x2="13.5" y2="12.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const AlignLeftIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <line x1="1.5" y1="3" x2="13.5" y2="3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="1.5" y1="6.5" x2="9.5" y2="6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="1.5" y1="10" x2="11.5" y2="10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="1.5" y1="13.5" x2="7.5" y2="13.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const AlignCenterIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <line x1="1.5" y1="3" x2="13.5" y2="3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="3.5" y1="6.5" x2="11.5" y2="6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="2" y1="10" x2="13" y2="10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="4.5" y1="13.5" x2="10.5" y2="13.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const AlignRightIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <line x1="1.5" y1="3" x2="13.5" y2="3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="5.5" y1="6.5" x2="13.5" y2="6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="3.5" y1="10" x2="13.5" y2="10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="7.5" y1="13.5" x2="13.5" y2="13.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const LinkIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <path d="M6.4 8.6l2.2-2.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M8.5 10.7l-1.5 1.5a2.6 2.6 0 11-3.7-3.7l1.5-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M6.5 4.3l1.5-1.5a2.6 2.6 0 113.7 3.7L10.2 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const ClearIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <path d="M6.5 3l5.5 5.5-3.5 3.5h-3l-3-3 4-4z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <line x1="3.5" y1="12" x2="12" y2="12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
